import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { PageSEO } from "../hooks/useSEO";
import "../styles/styles.css";

/* ── Formulario de contacto ── */
const ContactForm = ({ isEN }) => {
  const [fields, setFields] = useState({ name: "", email: "", subject: "general", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const subjects = isEN
    ? ["General inquiry", "Creator pitch", "Press / Media", "Technical issue", "Partnership"]
    : ["Consulta general", "Propuesta de creator", "Prensa / Medios", "Problema técnico", "Colaboración"];

  const subjectKeys = ["general", "creator", "press", "tech", "partner"];

  const handleChange = (e) => setFields({ ...fields, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fields.name.trim() || !fields.email.trim() || !fields.message.trim()) {
      setError(isEN ? "Please fill all required fields." : "Por favor rellena todos los campos.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
      setError(isEN ? "Please enter a valid email." : "Introduce un email válido.");
      return;
    }
    const subjectLabel = subjects[subjectKeys.indexOf(fields.subject)] || fields.subject;
    const body = `Nombre: ${fields.name}\nEmail: ${fields.email}\nAsunto: ${subjectLabel}\n\n${fields.message}`;
    window.location.href = `mailto:aiveoaicinema@gmail.com?subject=[AIVEO] ${encodeURIComponent(subjectLabel)}&body=${encodeURIComponent(body)}`;
    setSent(true);
    setError("");
  };

  if (sent) return (
    <div className="contact-success">
      <span className="contact-success-icon">✓</span>
      <strong>{isEN ? "Your email client should have opened." : "Se ha abierto tu cliente de email."}</strong>
      <p>{isEN ? "If not, write to us directly at aiveoaicinema@gmail.com" : "Si no, escríbenos a aiveoaicinema@gmail.com"}</p>
    </div>
  );

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form-row">
        <div className="contact-field">
          <label htmlFor="cf-name">{isEN ? "Name" : "Nombre"} *</label>
          <input id="cf-name" type="text" name="name" value={fields.name} onChange={handleChange} placeholder={isEN ? "Your name" : "Tu nombre"} />
        </div>
        <div className="contact-field">
          <label htmlFor="cf-email">Email *</label>
          <input id="cf-email" type="email" name="email" value={fields.email} onChange={handleChange} placeholder="tu@email.com" />
        </div>
      </div>
      <div className="contact-field">
        <label htmlFor="cf-subject">{isEN ? "Subject" : "Asunto"}</label>
        <select id="cf-subject" name="subject" value={fields.subject} onChange={handleChange}>
          {subjectKeys.map((k, i) => <option key={k} value={k}>{subjects[i]}</option>)}
        </select>
      </div>
      <div className="contact-field">
        <label htmlFor="cf-message">{isEN ? "Message" : "Mensaje"} *</label>
        <textarea id="cf-message" name="message" value={fields.message} onChange={handleChange} rows="5" placeholder={isEN ? "Tell us what you need..." : "Cuéntanos qué necesitas..."} />
      </div>
      {error && <p className="contact-error">{error}</p>}
      <button type="submit" className="btn-primary">{isEN ? "Send message" : "Enviar mensaje"}</button>
      <p className="recaptcha-notice" style={{ marginTop: "0.75rem" }}>
        {isEN
          ? "This sends a pre-composed email via your email client. No data is stored on our servers."
          : "Esto abre un email pre-compuesto en tu cliente de email. No se almacenan datos en nuestros servidores."}
      </p>
    </form>
  );
};

const About = () => {
  const { lang } = useLang();
  const isEN = lang === "en";

  return (
    <div className="legal-page">
      <PageSEO
        title={isEN ? "About AIVEO" : "Sobre AIVEO"}
        description={isEN
          ? "AIVEO is an AI cinema platform for original series, movies, books and comics created with artificial intelligence."
          : "AIVEO es una plataforma de cine IA para series, películas, libros y cómics originales creados con inteligencia artificial."}
        url="/about"
      />
      <div className="legal-container">

        <div className="legal-hero">
          <span className="legal-badge">BETA</span>
          <h1>{isEN ? "About AIVEO" : "Sobre AIVEO"}</h1>
          <p className="legal-lead">
            {isEN
              ? "AIVEO is a streaming platform for original content created with artificial intelligence — series, movies, books and comics that only exist here."
              : "AIVEO es una plataforma de streaming para contenido original creado con inteligencia artificial — series, películas, libros y cómics que solo existen aquí."}
          </p>
        </div>

        <section className="legal-section">
          <h2>{isEN ? "What is AIVEO?" : "¿Qué es AIVEO?"}</h2>
          <p>
            {isEN
              ? "AIVEO (AI Video Entertainment Online) is an experimental AI cinema platform currently in public beta. Our mission is to explore artificial intelligence as a creative tool for generating original audiovisual narratives: series with complete episodes, full-length movies, illustrated books and graphic novels, all created with the assistance of advanced AI models."
              : "AIVEO (AI Video Entertainment Online) es una plataforma experimental de cine con IA actualmente en beta pública. Nuestra misión es explorar la inteligencia artificial como herramienta creativa para generar narrativas audiovisuales originales: series con episodios completos, películas de largometraje, libros ilustrados y novelas gráficas, todos creados con la asistencia de modelos de IA avanzados."}
          </p>
          <p>
            {isEN
              ? "We are not Netflix. We are not HBO Max. We are not Amazon Prime Video. We are a small team exploring what comes next — a platform where AI becomes the author, the director, the illustrator and the composer, guided by human creative direction."
              : "No somos Netflix. No somos HBO Max. No somos Amazon Prime Video. Somos un equipo pequeño explorando lo que viene después: una plataforma donde la IA se convierte en el autor, el director, el ilustrador y el compositor, guiados por dirección creativa humana."}
          </p>
        </section>

        <section className="legal-section">
          <h2>{isEN ? "Our Approach" : "Nuestro Enfoque"}</h2>
          <div className="about-grid">
            {[
              {
                icon: "🤖",
                title: isEN ? "AI-Generated Content" : "Contenido Generado por IA",
                text: isEN
                  ? "Every story, script, image and audio on AIVEO is produced with the assistance of artificial intelligence tools. Human editors review and curate all content before publication."
                  : "Cada historia, guión, imagen y audio en AIVEO se produce con la asistencia de herramientas de inteligencia artificial. Editores humanos revisan y curan todo el contenido antes de su publicación.",
              },
              {
                icon: "📡",
                title: isEN ? "Open to Creators" : "Abierto a Creadores",
                text: isEN
                  ? "Through our IA Creators program, independent creators can submit pitches and produce AI-assisted content for the platform, subject to our editorial review process."
                  : "A través de nuestro programa IA Creators, creadores independientes pueden enviar propuestas y producir contenido asistido por IA para la plataforma, sujeto a nuestro proceso de revisión editorial.",
              },
              {
                icon: "🧪",
                title: isEN ? "Beta Stage" : "Fase Beta",
                text: isEN
                  ? "AIVEO is in active development. Features, content and policies are subject to change. During this beta period, all content is free to access. Subscription models will be introduced after beta."
                  : "AIVEO está en desarrollo activo. Las funcionalidades, el contenido y las políticas están sujetos a cambios. Durante este período beta, todo el contenido el contenido es accesible de forma gratuita. Los modelos de suscripción se introducirán una vez finalice la beta.",
              },
            ].map((item) => (
              <div className="about-card" key={item.title}>
                <div className="about-card-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="legal-section">
          <h2>{isEN ? "Team & Contact" : "Equipo y Contacto"}</h2>
          <p>
            {isEN
              ? "AIVEO is an independent project built by a small team of AI enthusiasts, developers and storytellers. We are based in Spain and operate as an online platform under Spanish and European law."
              : "AIVEO es un proyecto independiente creado por un pequeño equipo de entusiastas de la IA, desarrolladores y narradores. Estamos basados en España y operamos como plataforma online bajo la legislación española y europea."}
          </p>
          <p>
            {isEN
              ? "For press inquiries, partnership proposals, technical support or any other question, use the form below or write directly to "
              : "Para consultas de prensa, propuestas de colaboración, soporte técnico o cualquier otra pregunta, usa el formulario a continuación o escribe directamente a "}
            <a href="mailto:aiveoaicinema@gmail.com">aiveoaicinema@gmail.com</a>.
          </p>
          <ContactForm isEN={isEN} />
        </section>

        <section className="legal-section">
          <h2>{isEN ? "Legal" : "Legal"}</h2>
          <p>
            {isEN
              ? "AIVEO operates under Spanish law. For our full terms of use, privacy policy and AI content policy, see the links below."
              : "AIVEO opera bajo legislación española. Para nuestros términos de uso completos, política de privacidad y política de contenido IA, consulta los enlaces a continuación."}
          </p>
          <div className="legal-links-row">
            <Link to="/terms">{isEN ? "Terms of Use" : "Términos de Uso"}</Link>
            <Link to="/privacy">{isEN ? "Privacy Policy" : "Política de Privacidad"}</Link>
            <Link to="/ai-policy">{isEN ? "AI Content Policy" : "Política de Contenido IA"}</Link>
          </div>
        </section>

      </div>
    </div>
  );
};

export default About;
